import React, { useEffect, useState } from "react";
import { Users, FileText, HandshakeIcon, CheckCircle2, Flag, Star, ShieldOff, ShieldCheck, EyeOff, Eye, BadgeCheck, Award, XCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, STATUS_LABEL } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { TrustBadge } from "@/components/TrustBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { timeAgo } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";

const STAT_META = [
  { key: "members", label: "Members", icon: Users, color: "bg-terracotta" },
  { key: "id_verified", label: "ID Verified", icon: BadgeCheck, color: "bg-[#1D6FB8]" },
  { key: "posts", label: "Posts", icon: FileText, color: "bg-forest-700" },
  { key: "connections", label: "Connections", icon: HandshakeIcon, color: "bg-gold" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "bg-forest-600" },
  { key: "pending_verifications", label: "To verify", icon: ShieldCheck, color: "bg-terracotta" },
  { key: "fund_total", label: "Fund pledged", icon: Star, color: "bg-forest-700", money: true },
  { key: "open_reports", label: "Open reports", icon: Flag, color: "bg-destructive" },
];

export default function Admin() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [towns, setTowns] = useState([]);

  const loadAll = async () => {
    try {
      const [s, u, p, r, v, tw] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/posts"),
        api.get("/admin/reports"), api.get("/admin/verifications"), api.get("/admin/towns"),
      ]);
      setStats(s.data); setUsers(u.data); setPosts(p.data); setReports(r.data); setVerifications(v.data); setTowns(tw.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  const toggleSuspend = async (u) => {
    try { await api.patch(`/admin/users/${u.user_id}/suspend`, { status: u.suspended ? "unsuspend" : "suspend" }); toast.success(u.suspended ? "Member restored" : "Member suspended"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleChampion = async (u) => {
    try { await api.patch(`/admin/users/${u.user_id}/champion`, { status: u.champion ? "revoke" : "award" }); toast.success(u.champion ? "Champion removed" : "Champion awarded 🏆"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setRole = async (u, promote) => {
    try { await api.patch(`/admin/users/${u.user_id}/role`, { status: promote ? "promote" : "demote" }); toast.success(promote ? `${u.name} is now an admin` : "Admin access removed"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleHide = async (p) => {
    try { await api.patch(`/admin/posts/${p.post_id}/hide`, { status: p.hidden ? "show" : "hide" }); toast.success(p.hidden ? "Post visible" : "Post hidden"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resolveReport = async (r) => {
    try { await api.patch(`/admin/reports/${r.report_id}`, { status: "resolved" }); toast.success("Report resolved"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resolveVerification = async (v, approve) => {
    try { await api.patch(`/admin/verifications/${v.req_id}`, { status: approve ? "approve" : "reject" }); toast.success(approve ? "Member verified 🔵" : "Request rejected"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const townAction = async (t, approve) => {
    try { await api.patch(`/admin/towns/${t.town_id}`, { status: approve ? "approve" : "reject" }); toast.success(approve ? "Town approved 🌍" : "Town rejected"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pendingV = verifications.filter((v) => v.status === "pending").length;
  const openR = reports.filter((r) => r.status === "open").length;

  return (
    <div>
      <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900 mb-6">Admin dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
        {STAT_META.map((m) => (
          <div key={m.key} className="bg-white rounded-2xl border-2 border-border shadow-sm p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl ${m.color} flex items-center justify-center shrink-0`}>
              <m.icon size={24} strokeWidth={2.5} className="text-white" />
            </div>
            <div>
              <p className="font-head text-3xl font-bold text-forest-900">{stats ? (m.money ? Number(stats[m.key] || 0).toLocaleString() : stats[m.key]) : "–"}</p>
              <p className="text-sm text-forest-500 font-semibold">{m.label}</p>
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="verifications">
        <TabsList className="bg-white border-2 border-border rounded-full p-1.5 h-auto flex-wrap">
          <TabsTrigger value="verifications" data-testid="tab-verifications" className="rounded-full px-5 py-2.5 text-base font-semibold data-[state=active]:bg-terracotta data-[state=active]:text-white">Verifications {pendingV > 0 && `(${pendingV})`}</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members" className="rounded-full px-5 py-2.5 text-base font-semibold data-[state=active]:bg-terracotta data-[state=active]:text-white">Members</TabsTrigger>
          <TabsTrigger value="posts" data-testid="tab-posts" className="rounded-full px-5 py-2.5 text-base font-semibold data-[state=active]:bg-terracotta data-[state=active]:text-white">Posts</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-full px-5 py-2.5 text-base font-semibold data-[state=active]:bg-terracotta data-[state=active]:text-white">Reports {openR > 0 && `(${openR})`}</TabsTrigger>
          <TabsTrigger value="towns" data-testid="tab-towns" className="rounded-full px-5 py-2.5 text-base font-semibold data-[state=active]:bg-terracotta data-[state=active]:text-white">Towns {towns.filter((t) => t.status === "pending").length > 0 && `(${towns.filter((t) => t.status === "pending").length})`}</TabsTrigger>
        </TabsList>

        <TabsContent value="towns" className="mt-5 space-y-3" data-testid="admin-towns">
          {towns.length === 0 ? <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">No town requests yet.</p> :
            towns.map((t) => (
              <div key={t.town_id} className="bg-white rounded-2xl border-2 border-border p-4 flex items-center gap-4">
                <div className="min-w-0">
                  <p className="font-bold text-forest-900">{t.name} <span className="text-sm font-normal text-forest-500">· {t.region} · by {t.created_by_name}</span></p>
                  <p className="text-forest-700 text-sm">{t.description}</p>
                  <p className="text-sm text-forest-500 mt-1">Status: {t.status}</p>
                </div>
                {t.status === "pending" && (
                  <div className="ml-auto shrink-0 flex gap-2">
                    <button onClick={() => townAction(t, true)} data-testid={`town-approve-${t.town_id}`} className="min-h-[48px] px-4 rounded-full bg-forest-700 text-white font-semibold flex items-center gap-1.5"><CheckCircle2 size={18} strokeWidth={2.5} /> Approve</button>
                    <button onClick={() => townAction(t, false)} data-testid={`town-reject-${t.town_id}`} className="min-h-[48px] px-4 rounded-full border-2 border-destructive text-destructive font-semibold flex items-center gap-1.5"><XCircle size={18} strokeWidth={2.5} /></button>
                  </div>
                )}
              </div>
            ))}
        </TabsContent>

        <TabsContent value="verifications" className="mt-5 space-y-3" data-testid="admin-verifications">
          {verifications.length === 0 ? <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">No verification requests.</p> :
            verifications.map((v) => (
              <div key={v.req_id} className="bg-white rounded-2xl border-2 border-border p-4 flex items-center gap-4">
                <ShieldCheck size={22} strokeWidth={2.5} className={v.status === "pending" ? "text-terracotta" : "text-forest-500"} />
                <div className="min-w-0">
                  <p className="font-bold text-forest-900">{v.name} <span className="text-sm font-normal text-forest-500">· {v.email}</span></p>
                  {v.note && <p className="text-forest-700 text-sm">{v.note}</p>}
                  <p className="text-sm text-forest-500">Status: {v.status} · {timeAgo(v.created_at)}</p>
                </div>
                {v.status === "pending" && (
                  <div className="ml-auto shrink-0 flex gap-2">
                    <button onClick={() => resolveVerification(v, true)} data-testid={`verify-approve-${v.req_id}`}
                      className="min-h-[48px] px-4 rounded-full bg-forest-700 text-white font-semibold flex items-center gap-1.5 hover:bg-forest-900 transition-colors"><CheckCircle2 size={18} strokeWidth={2.5} /> Approve</button>
                    <button onClick={() => resolveVerification(v, false)} data-testid={`verify-reject-${v.req_id}`}
                      className="min-h-[48px] px-4 rounded-full border-2 border-destructive text-destructive font-semibold flex items-center gap-1.5 hover:bg-destructive hover:text-white transition-colors"><XCircle size={18} strokeWidth={2.5} /> Reject</button>
                  </div>
                )}
              </div>
            ))}
        </TabsContent>

        <TabsContent value="members" className="mt-5 space-y-3" data-testid="admin-members">
          {users.map((u) => (
            <div key={u.user_id} className="bg-white rounded-2xl border-2 border-border p-4 flex items-center gap-4 flex-wrap">
              <Avatar user={u} size={48} />
              <div className="min-w-0">
                <p className="font-bold text-forest-900 flex items-center gap-2 flex-wrap">{u.name}
                  {u.role === "admin" && <span className="text-xs bg-forest-900 text-white px-2 py-0.5 rounded-full">Admin</span>}
                  {u.suspended && <span className="text-xs bg-destructive text-white px-2 py-0.5 rounded-full">Suspended</span>}
                </p>
                <div className="flex items-center gap-2 mt-0.5"><TrustBadge level={u.trust_level} size="sm" /><span className="text-sm text-forest-500 truncate">{u.email}</span></div>
              </div>
              {u.role === "admin" ? (
                <div className="ml-auto shrink-0 flex gap-2 items-center flex-wrap">
                  {u.user_id === me?.user_id
                    ? <span className="text-sm text-forest-500 font-semibold px-3">That's you</span>
                    : <button onClick={() => setRole(u, false)} data-testid={`demote-${u.user_id}`}
                        className="min-h-[48px] px-4 rounded-full font-semibold border-2 border-destructive text-destructive flex items-center gap-1.5 hover:bg-destructive hover:text-white transition-colors">
                        <ShieldOff size={18} strokeWidth={2.5} /> Remove admin
                      </button>}
                </div>
              ) : (
                <div className="ml-auto shrink-0 flex gap-2 items-center flex-wrap">
                  <button onClick={() => setRole(u, true)} data-testid={`promote-${u.user_id}`}
                    className="min-h-[48px] px-4 rounded-full font-semibold bg-forest-900 text-white flex items-center gap-1.5 hover:bg-forest-700 transition-colors">
                    <Shield size={18} strokeWidth={2.5} /> Make admin
                  </button>
                  <button onClick={() => toggleChampion(u)} data-testid={`champion-${u.user_id}`}
                    className={`min-h-[48px] px-4 rounded-full font-semibold border-2 flex items-center gap-1.5 transition-colors ${u.champion ? "bg-gold text-forest-900 border-gold" : "bg-white text-forest-700 border-border hover:border-gold"}`}>
                    <Award size={18} strokeWidth={2.5} /> {u.champion ? "Champion" : "Award"}
                  </button>
                  <button onClick={() => toggleSuspend(u)} data-testid={`suspend-${u.user_id}`}
                    className={`min-h-[48px] px-4 rounded-full font-semibold border-2 flex items-center gap-1.5 transition-colors ${u.suspended ? "bg-forest-700 text-white border-forest-700" : "bg-white text-destructive border-destructive hover:bg-destructive hover:text-white"}`}>
                    {u.suspended ? <><ShieldCheck size={18} strokeWidth={2.5} /> Restore</> : <><ShieldOff size={18} strokeWidth={2.5} /> Suspend</>}
                  </button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="posts" className="mt-5 space-y-3" data-testid="admin-posts">
          {posts.map((p) => (
            <div key={p.post_id} className="bg-white rounded-2xl border-2 border-border p-4 flex items-center gap-4">
              <div className="min-w-0">
                <p className="font-bold text-forest-900 truncate">{p.title} {p.hidden && <span className="text-xs bg-destructive text-white px-2 py-0.5 rounded-full">Hidden</span>}</p>
                <p className="text-sm text-forest-500">{p.author_name} · {STATUS_LABEL[p.status]} · {timeAgo(p.created_at)}</p>
              </div>
              <button onClick={() => toggleHide(p)} data-testid={`hide-${p.post_id}`}
                className={`ml-auto shrink-0 min-h-[48px] px-5 rounded-full font-semibold border-2 flex items-center gap-2 transition-colors ${p.hidden ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-border hover:border-destructive hover:text-destructive"}`}>
                {p.hidden ? <><Eye size={18} strokeWidth={2.5} /> Unhide</> : <><EyeOff size={18} strokeWidth={2.5} /> Hide</>}
              </button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-5 space-y-3" data-testid="admin-reports">
          {reports.length === 0 ? <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">No reports.</p> :
            reports.map((r) => (
              <div key={r.report_id} className="bg-white rounded-2xl border-2 border-border p-4 flex items-start gap-4">
                <Flag size={22} strokeWidth={2.5} className={r.status === "open" ? "text-destructive mt-1" : "text-forest-500 mt-1"} />
                <div className="min-w-0">
                  <p className="font-bold text-forest-900">{r.target_type} report <span className="text-sm font-normal text-forest-500">by {r.reporter_name} · {timeAgo(r.created_at)}</span></p>
                  <p className="text-forest-700">{r.reason}</p>
                  <p className="text-sm text-forest-500 mt-1">Status: {r.status}</p>
                </div>
                {r.status === "open" && (
                  <button onClick={() => resolveReport(r)} data-testid={`resolve-${r.report_id}`}
                    className="ml-auto shrink-0 min-h-[48px] px-5 rounded-full font-semibold bg-forest-700 text-white flex items-center gap-2 hover:bg-forest-900 transition-colors">
                    <CheckCircle2 size={18} strokeWidth={2.5} /> Resolve
                  </button>
                )}
              </div>
            ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
